import { useEffect, useRef, useState } from "react";
import { Check, Copy, MessageCircle, Upload, ArrowLeft, CheckCircle2, ArrowRight, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { track, vid, source, rs, inputCls, LEAD_DONE } from "./parts";

const ORDER_KEY = "sb_order";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    const ta = document.createElement("textarea");
    ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select(); ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy"); document.body.removeChild(ta); return ok;
  }
}

function useCopy(c) {
  const [done, setDone] = useState("");
  return [done, async (key, text, msg) => {
    if (await copyText(text)) { setDone(key); toast.success(msg || c.copied); setTimeout(() => setDone(""), 1800); } else toast.error(c.copyFail);
  }];
}

const Steps = ({ c, step }) => (
  <div className="flex items-center gap-1.5" data-testid="checkout-steps">
    {c.steps.map((l, i) => (
      <div key={l} className="flex-1">
        <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#0BAF9A]" : "bg-[#E1ECEF]"}`} />
        <div className={`text-[10px] font-bold mt-1 ${i === step ? "text-[#102B3F]" : "text-[#9AA8B1]"}`}>{i + 1}. {l}</div>
      </div>
    ))}
  </div>
);

function ProductSummary({ c }) {
  const [o, setO] = useState(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    api.get("/public/offer").then((r) => setO(r.data)).catch(() => {});
    api.get("/public/orders-recent").then((r) => setN(r.data.count)).catch(() => {});
  }, []);
  if (!o) return <div className="h-[92px] rounded-2xl bg-[#F4F8F9] animate-pulse" />;
  return (
    <div className="space-y-1.5">
      <div className="rounded-2xl bg-[#F4F8F9] border border-[#E1ECEF] p-3 flex items-start gap-3" data-testid="checkout-product-summary">
        <div className="min-w-0 flex-1">
          <div className="font-display font-extrabold text-[15px] leading-tight text-[#102B3F]" data-testid="checkout-product-name">{o.product_name}</div>
          {o.product_description && <p className="text-[12px] leading-snug text-[#5C6B73] mt-0.5" data-testid="checkout-product-desc">{o.product_description}</p>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-extrabold text-xl text-[#102B3F]" data-testid="checkout-product-price">{rs(o.price)}</div>
          <div className="text-[10px] font-bold text-[#088F7E] uppercase tracking-wide">{c.oneTime}</div>
        </div>
      </div>
      {n > 0 && <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#36495A]" data-testid="checkout-recent-orders"><span className="h-2 w-2 rounded-full bg-[#0BAF9A] animate-pulse" />{n === 1 ? c.recent1 : c.recent.replace("{n}", n)}</div>}
    </div>
  );
}

function DetailsStep({ c, prefill, lang, onCreated, price }) {
  const [f, setF] = useState({ full_name: prefill?.full_name || "", whatsapp: prefill?.whatsapp || "", whatsapp_consent: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const err = f.full_name.trim().length < 2 ? c.invalidName : f.whatsapp.replace(/\D/g, "").length < 7 ? c.invalidPhone : null;
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/orders", { ...f, lang, visitor_id: vid(), source: source(), capture: prefill?.capture || "purchase_form" });
      localStorage.setItem(ORDER_KEY, JSON.stringify({ id: data.order_id, token: data.order_token }));
      localStorage.setItem(LEAD_DONE, "1");
      onCreated(data);
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="checkout-details-form">
      <input required autoComplete="name" placeholder={c.fullName} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className={inputCls} data-testid="checkout-name-input" />
      <input required type="tel" inputMode="tel" autoComplete="tel" placeholder={c.whatsapp} value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} className={inputCls} data-testid="checkout-whatsapp-input" />
      <label className="flex gap-2.5 items-start px-1 cursor-pointer" data-testid="checkout-consent-label">
        <input type="checkbox" checked={f.whatsapp_consent} onChange={(e) => setF({ ...f, whatsapp_consent: e.target.checked })} className="mt-0.5 h-5 w-5 shrink-0 accent-[#0BAF9A]" data-testid="checkout-consent-checkbox" />
        <span className="text-[12px] leading-snug text-[#36495A]">{c.consent}</span>
      </label>
      <button disabled={busy} className="lp-btn w-full min-h-[54px] text-[16px]" data-testid="checkout-next-btn">{busy ? c.wait : `${c.next}${price ? ` — ${rs(price)}` : ""}`} <ArrowRight className="h-4 w-4" /></button>
      <p className="text-center text-[13px] text-[#5C6B73]" data-testid="checkout-login-hint">{c.haveAcc} <a href="/login" className="font-bold text-[#088F7E] inline-block min-h-[36px] leading-[36px]" data-testid="checkout-login-link">{c.logIn}</a></p>
    </form>
  );
}

const Row = ({ label, value, onCopy, copied, c, k }) => (
  <div className="flex items-center gap-2 py-2.5 border-t border-[#E1ECEF] first:border-0">
    <div className="min-w-0 flex-1"><div className="text-[11px] font-semibold text-[#7D8C96]">{label}</div><div className="font-bold text-[15px] text-[#102B3F] break-all select-all" data-testid={`checkout-${k}`}>{value}</div></div>
    {onCopy && <button type="button" onClick={onCopy} className={`h-9 px-3 rounded-xl text-xs font-bold inline-flex items-center gap-1 shrink-0 border ${copied ? "bg-[#0BAF9A] border-[#0BAF9A] text-white" : "border-[#D5E3E8] text-[#102B3F] hover:border-[#0BAF9A]"}`} data-testid={`checkout-copy-${k}`}>{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{copied ? c.copied : c.copy}</button>}
  </div>
);

function PaymentStep({ c, order, onNext }) {
  const p = order.payment;
  const [done, copy] = useCopy(c);
  const all = `${c.total}: ${rs(p.amount)}\n${c.company}: ${p.company_name}\n${c.bank}: ${p.bank_name}\n${c.account}: ${p.bank_account}\n${c.juice}: ${p.juice_number}`;
  return (
    <div className="space-y-3" data-testid="checkout-payment-step">
      <div className="rounded-2xl bg-[#102B3F] text-white px-4 py-3 flex items-center justify-between"><span className="text-sm text-white/70">{c.total}</span><span className="font-display font-extrabold text-2xl" data-testid="checkout-amount">{rs(p.amount)}</span></div>
      <div className="rounded-2xl border border-[#E1ECEF] px-4">
        <Row c={c} k="company" label={c.company} value={p.company_name} />
        <Row c={c} k="bank" label={c.bank} value={p.bank_name} />
        <Row c={c} k="account" label={c.account} value={p.bank_account} copied={done === "account"} onCopy={() => copy("account", p.bank_account)} />
        <Row c={c} k="juice" label={c.juice} value={p.juice_number} copied={done === "juice"} onCopy={() => copy("juice", p.juice_number)} />
      </div>
      <button type="button" onClick={() => copy("all", all, c.copiedAll)} className="w-full h-11 rounded-xl border border-[#102B3F] text-[#102B3F] font-bold text-sm inline-flex items-center justify-center gap-2" data-testid="checkout-copy-all">{done === "all" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{c.copyAll}</button>
      <p className="text-[12px] text-[#5C6B73]">{c.payNote}</p>
      <button onClick={onNext} className="lp-btn w-full min-h-[50px]" data-testid="checkout-paid-btn">{c.paid} <ArrowRight className="h-4 w-4" /></button>
    </div>
  );
}

function UploadProof({ c, order, onDone }) {
  const ref = useRef(null);
  const [method, setMethod] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|pdf)$/i.test(file.name) || file.size > 10 * 1024 * 1024) { toast.error(c.badType); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("token", order.token); fd.append("method", method);
    setBusy(true);
    try { const { data } = await api.post(`/public/orders/${order.order_id}/proof`, fd); onDone(data.receipt_reference); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2"><span className="text-[11px] font-semibold text-[#7D8C96]">{c.method}</span>
        {c.methods.map(([k, l]) => <button key={k} type="button" onClick={() => setMethod(method === k ? "" : k)} className={`lp-chip min-h-[32px] px-3 text-xs ${method === k ? "lp-chip-on" : "lp-chip-off"}`} data-testid={`checkout-method-${k}`}>{l}</button>)}</div>
      <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden" onChange={(e) => send(e.target.files?.[0])} data-testid="checkout-proof-file-input" />
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="w-full h-12 rounded-xl bg-[#102B3F] text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-upload-btn"><Upload className="h-4 w-4" />{busy ? c.uploading : c.upload}</button>
      <p className="text-[11px] text-[#7D8C96] text-center">{c.types}</p>
    </div>
  );
}

function AccountOffer({ c, order, lang, onCreated }) {
  const [openForm, setOpenForm] = useState(false);
  const [pw, setPw] = useState("");
  const [email, setEmail] = useState(order.email || "");
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(order.account_created);
  const finish = (token) => { localStorage.setItem("sb_token", token); setDone(true); onCreated(); window.location.replace("/"); };
  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!EMAIL_RE.test(email.trim())) { toast.error(c.invalidEmail); return; }
    setBusy(true);
    try {
      if (exists) {
        const { data } = await api.post("/auth/login", { email: email.trim(), password: pw });
        localStorage.setItem("sb_token", data.token);
        await api.post(`/public/orders/${order.order_id}/link`, { token: order.token });
        finish(data.token);
      } else {
        const { data } = await api.post(`/public/orders/${order.order_id}/account`, { token: order.token, email: email.trim(), password: pw, language: lang });
        finish(data.token);
      }
    } catch (er) {
      if (er.response?.status === 409) { setExists(true); setPw(""); } else toast.error(errMsg(er));
    } finally { setBusy(false); }
  };
  if (done) return (
    <div className="rounded-2xl bg-[#EAF8F4] p-3.5 text-left space-y-2" data-testid="checkout-account-done">
      <p className="text-[13px] text-[#102B3F]">{c.acctDone}</p>
      <a href="/" className="text-sm font-bold text-[#088F7E] underline underline-offset-2" data-testid="checkout-account-go">{c.acctGo}</a>
    </div>
  );
  return (
    <div className="rounded-2xl border border-dashed border-[#0BAF9A] p-3.5 text-left space-y-2" data-testid="checkout-account-offer">
      <div className="font-display font-bold text-[#102B3F] flex items-center gap-2"><UserPlus className="h-4 w-4 text-[#0BAF9A]" />{c.acctH}</div>
      <p className="text-[12px] text-[#5C6B73]">{c.acctP}</p>
      {!openForm ? <button type="button" onClick={() => setOpenForm(true)} className="w-full h-11 rounded-xl border border-[#102B3F] text-[#102B3F] font-bold text-sm" data-testid="checkout-account-open-btn">{c.acctBtn}</button> : (
        <form onSubmit={create} className="space-y-2">
          <div className="text-[12px] text-[#36495A] rounded-xl bg-[#F4F8F9] px-3 py-2" data-testid="checkout-account-identity"><b>{order.full_name}</b></div>
          <input required type="email" inputMode="email" autoComplete="email" placeholder={c.email} value={email} onChange={(e) => { setEmail(e.target.value); setExists(false); }} className={inputCls} data-testid="checkout-account-email-input" />
          {exists && <p className="text-[12px] font-semibold text-[#102B3F] rounded-xl bg-[#EAF8F4] px-3 py-2" data-testid="checkout-account-exists">{c.acctExists}</p>}
          <input type="password" required minLength={exists ? 1 : 8} autoComplete={exists ? "current-password" : "new-password"} placeholder={exists ? c.loginPw : c.password} value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="checkout-account-password-input" />
          <button disabled={busy} className="lp-btn w-full min-h-[46px] text-sm" data-testid="checkout-account-submit-btn">{busy ? c.acctBusy : exists ? c.acctLogin : c.acctBtn}</button>
        </form>
      )}
    </div>
  );
}

function ProofStep({ c, order, lang, onBack, onDone, onAccount }) {
  const p = order.payment;
  const msg = `${c.waMsg.replace("{amount}", p.amount).replace("{product}", p.product_name)} ${c.orderRef}: ${order.order_id.slice(0, 8).toUpperCase()}`;
  const wa = `https://wa.me/${p.proof_whatsapp_number}?text=${encodeURIComponent(msg)}`;
  return (
    <div className="space-y-3" data-testid="checkout-proof-step">
      <p className="font-display font-bold text-[#102B3F] leading-snug">{c.proofH}</p>
      <div className="rounded-2xl border border-[#E1ECEF] p-3.5 space-y-2">
        <div className="text-[10px] font-bold tracking-widest text-[#088F7E] uppercase">{c.optA}</div>
        <a href={wa} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_redirect", { lead_id: order.order_id, capture: order.capture })} className="w-full h-12 rounded-xl bg-[#25D366] hover:bg-[#1EBE5A] text-white font-bold text-sm inline-flex items-center justify-center gap-2" data-testid="checkout-proof-whatsapp-btn"><MessageCircle className="h-4 w-4" />{c.sendWa}</a>
        <p className="text-[11px] text-[#7D8C96] text-center">{c.attach}</p>
      </div>
      <div className="rounded-2xl border border-[#E1ECEF] p-3.5 space-y-2">
        <div className="text-[10px] font-bold tracking-widest text-[#088F7E] uppercase">{c.optB}</div>
        <UploadProof c={c} order={order} onDone={onDone} />
      </div>
      <AccountOffer c={c} order={order} lang={lang} onCreated={onAccount} />
      <button onClick={onBack} className="text-sm font-semibold text-[#5C6B73] inline-flex items-center gap-1" data-testid="checkout-back-btn"><ArrowLeft className="h-4 w-4" />{c.back}</button>
    </div>
  );
}

const Done = ({ c, refNo, order, lang, onAccount }) => (
  <div className="text-center py-2 space-y-3" data-testid="checkout-done">
    <CheckCircle2 className="h-14 w-14 text-[#0BAF9A] mx-auto" />
    <div className="font-display font-extrabold text-xl text-[#102B3F]">{c.doneH}</div>
    <p className="text-sm text-[#4A5D6A]">{c.doneP} <b data-testid="checkout-receipt-ref">{refNo}</b></p>
    <AccountOffer c={c} order={order} lang={lang} onCreated={onAccount} />
  </div>
);

export function CheckoutModal({ open, onOpenChange, t, lang, prefill }) {
  const c = t.checkout;
  const [step, setStep] = useState(0);
  const [order, setOrder] = useState(null);
  const [refNo, setRefNo] = useState("");
  const [price, setPrice] = useState(null);
  useEffect(() => { if (open) api.get("/public/offer").then((r) => setPrice(r.data.price)).catch(() => {}); }, [open]);
  useEffect(() => {
    if (!open || order) return;
    const saved = JSON.parse(localStorage.getItem(ORDER_KEY) || "null");
    if (!saved) return;
    api.get(`/public/orders/${saved.id}`, { params: { token: saved.token } })
      .then((r) => { if (r.data.payment_status === "pending") { setOrder({ ...r.data, token: saved.token }); setStep(r.data.proof_submitted ? 2 : 1); } else localStorage.removeItem(ORDER_KEY); })
      .catch(() => localStorage.removeItem(ORDER_KEY));
  }, [open, order]);
  const markAccount = () => setOrder((o) => ({ ...o, account_created: true }));
  const reset = () => { localStorage.removeItem(ORDER_KEY); setOrder(null); setStep(0); setRefNo(""); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-md rounded-3xl bg-white p-5 sm:p-6 gap-4 max-h-[92dvh] overflow-y-auto" data-testid="lead-modal">
        <div className="pr-8">
          <DialogTitle className="font-display text-xl font-extrabold text-[#102B3F]">{c.title}</DialogTitle>
          <DialogDescription className="text-xs mt-0.5">{order?.payment?.product_name}{order ? ` · ${c.orderRef} ${order.order_id.slice(0, 8).toUpperCase()}` : ""}</DialogDescription>
        </div>
        {!refNo && <Steps c={c} step={step} />}
        {refNo ? <Done c={c} refNo={refNo} order={order} lang={lang} onAccount={markAccount} />
          : step === 0 ? <><ProductSummary c={c} /><DetailsStep c={c} lang={lang} prefill={prefill} price={price} onCreated={(d) => { setOrder({ ...d, token: d.order_token, capture: prefill?.capture }); setStep(1); }} /></>
          : step === 1 ? <PaymentStep c={c} order={order} onNext={() => setStep(2)} />
          : <ProofStep c={c} order={order} lang={lang} onBack={() => setStep(1)} onDone={setRefNo} onAccount={markAccount} />}
        {order && step > 0 && !refNo && <button onClick={reset} className="text-[11px] text-[#9AA8B1] underline underline-offset-2" data-testid="checkout-new-order-btn">{c.newOrder}</button>}
      </DialogContent>
    </Dialog>
  );
}
